import { useCallback, useEffect, useRef, useState } from 'react';
import { Briefcase, ChevronRight, Loader2, MessageSquare, Send, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STATUS_STYLES = {
  open:        'bg-blue-500/10 text-blue-600 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  resolved:    'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  closed:      'bg-slate-500/10 text-slate-500 border-slate-500/20',
};
const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };

function Avatar({ name, role }) {
  const isCandidate = role === 'candidate';
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  return (
    <div className={cn(
      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
      isCandidate ? 'bg-primary/15 text-primary' : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    )}>
      {initials}
    </div>
  );
}

function MessageBubble({ msg, currentUserId }) {
  const isOwn = msg.senderId === currentUserId || msg.senderId?._id === currentUserId;
  const time  = msg.createdAt ? new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className={cn('flex items-end gap-2', isOwn ? 'flex-row-reverse' : 'flex-row')}>
      {!isOwn && <Avatar name={msg.senderName} role={msg.senderRole} />}
      <div className={cn('max-w-[75%] space-y-1', isOwn && 'items-end flex flex-col')}>
        {!isOwn && (
          <p className="text-[10px] text-muted-foreground ml-1">{msg.senderName}</p>
        )}
        <div className={cn(
          'rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
          isOwn
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm'
        )}>
          {msg.content}
        </div>
        <p className={cn('text-[10px] text-muted-foreground', isOwn ? 'text-right' : 'text-left')}>{time}</p>
      </div>
    </div>
  );
}

export default function CandidateTicketThread({ ticketId, open, onClose, currentUserId }) {
  const [ticket,    setTicket]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [reply,     setReply]     = useState('');
  const [sending,   setSending]   = useState(false);
  const bottomRef = useRef(null);

  const loadTicket = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await apiClient.getSupportTicketById(ticketId);
      setTicket(res.data);
    } catch {
      toast.error('Could not load ticket.');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { if (open) loadTicket(); }, [open, loadTicket]);

  // Scroll to bottom when messages load/update
  useEffect(() => {
    if (ticket?.messages?.length) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [ticket?.messages?.length]);

  const handleSend = async () => {
    if (!reply.trim() || sending) return;
    if (ticket?.status === 'closed') return toast.error('This ticket is closed.');
    setSending(true);
    try {
      await apiClient.addTicketMessage(ticket._id, { content: reply.trim() });
      setReply('');
      await loadTicket();
      toast.success('Reply sent.');
    } catch (err) {
      toast.error(err.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const isClosed = ticket?.status === 'closed';

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg" side="right">
        {/* Header */}
        <SheetHeader className="shrink-0 border-b border-border px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {ticket && (
                  <span className={cn(
                    'rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                    STATUS_STYLES[ticket.status] || STATUS_STYLES.open
                  )}>
                    {STATUS_LABELS[ticket.status] || ticket.status}
                  </span>
                )}
                {ticket?.ticketNumber && (
                  <span className="text-xs text-muted-foreground font-mono">{ticket.ticketNumber}</span>
                )}
              </div>
              <SheetTitle className="mt-1 text-sm leading-snug line-clamp-2">
                {loading ? 'Loading…' : (ticket?.subject || 'Support Ticket')}
              </SheetTitle>
              {ticket?.sessionTitle && (
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Briefcase className="h-3 w-3" />
                  {ticket.sessionTitle}
                </p>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && ticket?.messages?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <MessageSquare className="h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No messages yet</p>
            </div>
          )}
          {!loading && ticket?.messages?.map((msg, i) => (
            <MessageBubble key={i} msg={msg} currentUserId={currentUserId} />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Reply box */}
        <div className="shrink-0 border-t border-border bg-card px-4 py-3">
          {isClosed ? (
            <p className="text-center text-xs text-muted-foreground py-1">
              This ticket is closed. Raise a new ticket if you need further help.
            </p>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Type your reply… (Enter to send)"
                rows={2}
                disabled={sending}
                className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!reply.trim() || sending}
                className="h-9 w-9 shrink-0 rounded-xl"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
